import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  const sections = [
    {
      number: 1,
      title: 'Data We Collect',
      content: [
        'Account data: email address, authentication identifiers.',
        'User content: photos you upload for hair analysis and progress tracking.',
        'Device and app data: device identifiers (through Firebase), app diagnostics, crash and performance signals.',
        'Usage metadata: feature usage events, request timing, and service-level telemetry.',
        '',
        'We do not intentionally collect payment card data in this app flow.',
      ],
    },
    {
      number: 2,
      title: 'Why We Collect Data',
      content: [
        'Create and secure your account.',
        'Provide photo-based analysis and progress timelines.',
        'Generate AI insights and related app features.',
        'Maintain app reliability, prevent abuse, and troubleshoot issues.',
        'Improve product quality and user experience.',
      ],
    },
    {
      number: 3,
      title: 'How We Use and Share Data',
      content: [
        'We use data only for legitimate product and security purposes.',
        'We do not sell personal data to third parties.',
        'We may use service providers (for example, Firebase/Google Cloud and Supabase where configured) to host, process, and secure app functionality.',
        'Data sharing is limited to what is required to operate the service.',
      ],
    },
    {
      number: 4,
      title: 'Photo Storage and Protection',
      content: [
        'User photos are protected with authenticated access controls.',
        'Photos are uploaded over encrypted connections (HTTPS/TLS).',
        'Photos are stored under user-scoped storage paths.',
        'Access is restricted to authorized users and service operations.',
        'We apply validation and sanitization checks on uploads (file type/content checks and size limits).',
        'Public, unauthenticated photo access is not intended for user-private images.',
      ],
    },
    {
      number: 5,
      title: 'Authentication and Security',
      content: [
        'Authentication is handled with Firebase Auth.',
        'Access tokens are validated server-side before protected actions.',
        'Security controls include rate limiting, request validation, and production transport security controls.',
      ],
    },
    {
      number: 6,
      title: 'Data Retention',
      content: [
        'We retain data only as long as needed for:',
        '  • Providing the service,',
        '  • Meeting legal obligations,',
        '  • Resolving disputes,',
        '  • Enforcing agreements.',
        '',
        'When an account is deleted, associated profile and media records are scheduled for removal from active systems and connected storage.',
      ],
    },
    {
      number: 7,
      title: 'Account Deletion and Data Retention',
      content: [
        'How to Delete Your Account:',
        '• In-App: Open the HairRabbit app, go to Profile → Settings → Delete Account and confirm. This triggers immediate deletion of your records.',
        '• By Email: Contact us at hairrabbitofficial@gmail.com from your registered email address with the subject "Account Deletion Request".',
        '',
        'What Data is Deleted:',
        'Deletion removes your email, display name, Firebase UID, all uploaded hair photos, generated reels, and AI analysis reports from our primary systems.',
        '',
        'What is Kept:',
        'Residual copies may remain in secure, encrypted backups for up to 90 days for disaster recovery purposes before being purged. Non-identifiable diagnostic logs may be kept for service improvements.',
      ],
    },
    {
      number: 8,
      title: "Children's Privacy",
      content: [
        'HairRabbit is not directed to children under the applicable digital consent age in your region. If you believe a child has provided personal data, contact us for deletion.',
      ],
    },
    {
      number: 9,
      title: 'International Data Transfers',
      content: [
        'Your data may be processed in regions where our service providers operate. We apply reasonable safeguards aligned with applicable law.',
      ],
    },
    {
      number: 10,
      title: 'Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time. Material changes will be reflected by updating the effective date and, where required, additional notice.',
      ],
    },
    {
      number: 11,
      title: 'Contact Information',
      content: [
        'Privacy contact email: hairrabbitofficial@gmail.com',
        'Support email: hairrabbitofficial@gmail.com',
      ],
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F7F9FA',
      paddingTop: '24px',
      paddingBottom: '48px',
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        paddingLeft: '16px',
        paddingRight: '16px',
      }}>
        <button
          onClick={handleBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#0D9488',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '24px',
            padding: '8px',
            marginLeft: '-8px',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <ArrowLeft size={18} strokeWidth={1.75} />
          Back
        </button>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        paddingLeft: '16px',
        paddingRight: '16px',
      }}>
        {/* Title Section */}
        <div style={{
          marginBottom: '48px',
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
            margin: '0 0 12px',
            color: '#111827',
            letterSpacing: '-0.02em',
          }}>
            Privacy Policy
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#64748B',
            margin: 0,
            lineHeight: '1.6',
            fontFamily: 'Inter, sans-serif',
          }}>
            Effective date: March 18, 2026
          </p>
        </div>

        {/* Introduction */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '24px',
          border: '1px solid #E2E8F0',
          padding: '32px',
          marginBottom: '32px',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.02)',
        }}>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.6',
            color: '#475569',
            margin: 0,
            fontFamily: 'Inter, sans-serif',
          }}>
            HairRabbit provides AI-powered hair analysis and progress tracking.
            This Privacy Policy explains what information we collect, why we collect it, and how
            users can control and delete their data.
          </p>
        </div>

        {/* Sections */}
        <div style={{
          display: 'grid',
          gap: '24px',
        }}>
          {sections.map((section) => (
            <div
              key={section.number}
              style={{
                background: '#FFFFFF',
                borderRadius: '24px',
                border: '1px solid #E2E8F0',
                padding: '32px',
                boxShadow: '0 2px 20px rgba(0, 0, 0, 0.02)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.borderColor = '#0D9488';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 20px rgba(0, 0, 0, 0.02)';
                e.currentTarget.style.borderColor = '#E2E8F0';
              }}
            >
              <h2 style={{
                fontSize: '20px',
                fontWeight: 600,
                fontFamily: 'Manrope, sans-serif',
                margin: '0 0 16px',
                color: '#111827',
              }}>
                {section.number}. {section.title}
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}>
                {section.content.map((paragraph, idx) => (
                  <p
                    key={idx}
                    style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: paragraph === '' ? 'transparent' : '#475569',
                      margin: 0,
                      fontFamily: 'Inter, sans-serif',
                      height: paragraph === '' ? '8px' : 'auto',
                    }}
                  >
                    {paragraph || ' '}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Section */}
        <div style={{
          marginTop: '48px',
          paddingTop: '32px',
          borderTop: '1px solid #E2E8F0',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: '14px',
            color: '#64748B',
            margin: 0,
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.6',
          }}>
            Last updated: March 15, 2026 | For questions or concerns, please contact us at{' '}
            <a
              href="mailto:hairrabbitofficial@gmail.com"
              style={{
                color: '#0D9488',
                textDecoration: 'none',
                fontWeight: '500',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              hairrabbitofficial@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
